<template>
  <div class="about-page">
    <AppHeader />

    <!-- ===== 页面头部 ===== -->
    <section class="page-hero">
      <div class="hero-inner">
        <span class="hero-eyebrow">ABOUT US · 关于我们</span>
        <h1>智能化监测设备研发部</h1>
        <p class="hero-org">长三角碳纤维及复合材料技术创新中心 · 常州</p>
        <p class="hero-desc">面向工程结构服役安全，提供<strong>定制化监测设备</strong>、数据采集与<strong>智能诊断</strong>服务</p>
        <div class="hero-vision">
          <el-icon :size="20"><Promotion /></el-icon>
          <span>愿景：成为工程结构定制化监测设备与智能诊断服务领域的专业化技术"引领者"</span>
        </div>
      </div>
    </section>

    <!-- ===== 我们是谁 + 三大定位 ===== -->
    <section class="section section-intro">
      <div class="container">
        <div class="intro-head">
          <span class="eyebrow">Who We Are</span>
          <h2>我们是谁</h2>
        </div>
        <div class="intro-grid">
          <div class="intro-text">
            <p>
              团队<strong>起源于中冶（2016）</strong>的工程场景，<strong>发展于北京科技大学与哈尔滨工业大学（2020）</strong>，
              <strong>落地于长三角（2026）</strong>——经南京水科院胡亚安院士等专家推荐、江苏省产业技术研究院审核，
              正式入驻长三角碳纤维及复合材料技术创新中心，组建智能化监测设备研发部，开启产业化运营。
            </p>
            <p>
              中心由江苏产研院、常州市人民政府、常州高新区共同举办，具备政府产业平台与事业单位属性。
              我们主持或参与了大量工程结构测试与监测服务，主持国家自然科学基金重大项目、重大仪器项目、
              十三五与十四五重点专项等课题，形成"专家指导—高校研发—平台转化—工程服务"的产学研协同布局。
            </p>
            <div class="intro-quote">
              <el-icon :size="22"><InfoFilled /></el-icon>
              <span>为什么需要量身定制？——没有万能设备，只有面向场景的最优监测方案。</span>
            </div>
          </div>
          <div class="position-cards">
            <div class="position-card" v-for="item in positions" :key="item.title">
              <div class="position-icon"><el-icon :size="26"><component :is="item.icon" /></el-icon></div>
              <div>
                <h3>{{ item.title }}</h3>
                <p>{{ item.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== 数据带 ===== -->
    <section class="stats-band">
      <div class="container stats-grid">
        <div class="stat-item" v-for="stat in stats" :key="stat.label">
          <div class="stat-value">{{ stat.value }}<span>{{ stat.suffix }}</span></div>
          <div class="stat-label">{{ stat.label }}</div>
        </div>
      </div>
    </section>

    <!-- ===== 发展历程 ===== -->
    <section class="section section-history">
      <div class="container">
        <div class="intro-head">
          <span class="eyebrow">Milestones</span>
          <h2>发展历程</h2>
          <p class="head-sub">工程需求牵引 → 高校科研积累 → 产业化平台落地</p>
        </div>
        <div class="timeline">
          <div class="timeline-item" v-for="item in milestones" :key="item.year">
            <div class="timeline-year">{{ item.year }}</div>
            <div class="timeline-dot"></div>
            <div class="timeline-card">
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== 核心能力：产品体系 + 监测方向 ===== -->
    <section class="section section-capability">
      <div class="container">
        <div class="intro-head">
          <span class="eyebrow">Products &amp; Technology</span>
          <h2>三大产品体系 · 五大监测方向</h2>
          <p class="head-sub">标准产品为基础 · 微定制为特色 · 平台化与工程化交付为核心能力</p>
        </div>
        <div class="product-cards">
          <div class="product-card" v-for="item in productLines" :key="item.title">
            <div class="product-icon"><el-icon :size="28"><component :is="item.icon" /></el-icon></div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
            <div class="product-tags">
              <span v-for="tag in item.tags" :key="tag">{{ tag }}</span>
            </div>
          </div>
        </div>
        <div class="direction-strip">
          <div class="direction-item" v-for="dir in directions" :key="dir">
            <el-icon :size="18"><CircleCheck /></el-icon>{{ dir }}
          </div>
        </div>
        <div class="platform-line">
          <el-icon :size="20"><Connection /></el-icon>
          <span>自研物联网平台：统一接入 · 统一检测 · 统一预警 · 统一运维，支持 PC 大屏 / APP / 小程序多端查看</span>
        </div>
      </div>
    </section>

    <!-- ===== 典型案例（深色带） ===== -->
    <section class="section section-cases">
      <div class="container">
        <div class="intro-head light">
          <span class="eyebrow light">Case Studies</span>
          <h2>典型案例与业绩</h2>
          <p class="head-sub">从工业动载到城市应急，从自建房处置到人民大会堂——工程经验持续积累</p>
        </div>
        <div class="case-grid">
          <div class="case-card" v-for="item in cases" :key="item.title">
            <div class="case-tag">{{ item.tag }}</div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== 合作模式 ===== -->
    <section class="section section-coop">
      <div class="container">
        <div class="intro-head">
          <span class="eyebrow">Cooperation</span>
          <h2>合作模式</h2>
          <p class="head-sub">合作可由轻量试点逐步升级，也可根据项目需求直接选择合适模式</p>
        </div>
        <div class="coop-grid">
          <div class="coop-card" v-for="(item, idx) in coopModes" :key="item.title">
            <div class="coop-no">{{ String(idx + 1).padStart(2, '0') }}</div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== 为什么选择我们 ===== -->
    <section class="section section-why">
      <div class="container">
        <div class="intro-head">
          <span class="eyebrow">Why Us</span>
          <h2>为什么选择我们</h2>
        </div>
        <div class="why-grid">
          <div class="why-card" v-for="item in whyUs" :key="item.title">
            <div class="why-icon"><el-icon :size="24"><component :is="item.icon" /></el-icon></div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
        </div>
        <p class="why-quote">既有科研能力，又有工程效率；既能定制开发，又能快速落地。</p>
      </div>
    </section>

    <!-- ===== 联系 CTA ===== -->
    <section class="section section-contact">
      <div class="container contact-inner">
        <h2>有一个结构监测需求？</h2>
        <p>从需求研判、方案制定、设备定制到现场实施与持续服务，欢迎与我们交流</p>
        <div class="contact-actions">
          <el-button type="primary" size="large" round @click="copyEmail">
            <el-icon><Message /></el-icon> qingzhenjiance@163.com
          </el-button>
          <el-button size="large" round plain @click="goProducts">
            查看产品 <el-icon><ArrowRight /></el-icon>
          </el-button>
        </div>
        <p class="contact-addr"><el-icon><Location /></el-icon> 常州 · 长三角碳纤维及复合材料技术创新中心</p>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import AppHeader from '@/components/AppHeader.vue'
import {
  Cpu, EditPen, TrendCharts, Promotion, InfoFilled, CircleCheck, Connection,
  Box, VideoCamera, Aim, DataLine, Odometer, Microphone, Stopwatch,
  Message, ArrowRight, Location, OfficeBuilding, FirstAidKit, School
} from '@element-plus/icons-vue'

const router = useRouter()

const positions = ref([
  { icon: Cpu, title: '技术驱动型企业', desc: '以土木结构机理为基础，以监测硬件研发为支撑，以智能诊断算法为延伸' },
  { icon: EditPen, title: '定制化服务商', desc: '面向不同结构类型提供差异化方案，面向不同病害问题定制监测设备，面向现场条件优化布设、供电与通信方式' },
  { icon: TrendCharts, title: 'AI 赋能型组织', desc: '利用智能化办公提升方案设计效率，利用数据分析工具加快研发迭代，利用算法模型辅助状态识别与预警判断' }
])

const stats = ref([
  { value: '7', suffix: '亿元', label: '中心首个建设期计划投入' },
  { value: '1.5', suffix: '亿+', label: '在研项目研发资金' },
  { value: '10', suffix: '余项', label: '主持承担国家、省部级及重大横向项目' },
  { value: '3', suffix: '位院士', label: '战略指导' }
])

const milestones = ref([
  { year: '2016', title: '起源于中冶', desc: '工程需求牵引——从中冶工程场景与结构安全需求出发，在真实工程问题中形成研发方向，主持或参与大量工程结构测试与监测服务。' },
  { year: '2020', title: '发展于北科大 · 哈工大', desc: '高校科研积累——主持国家自然科学基金重大项目、重大仪器项目、面上项目与十三五、十四五重点专项，奠定智能化诊治算法与设备研发基础，服务城安院、宝武、中冶等单位。' },
  { year: '2026', title: '落地于长三角', desc: '产业化平台落地——经专家推荐与江苏省产业技术研究院审核，正式入驻长三角碳纤维及复合材料技术创新中心，组建智能化监测设备研发部，开启商业化运营。' }
])

const productLines = ref([
  { icon: Box, title: '微定制监测设备', desc: '以标准产品为基础、微定制为特色，覆盖裂缝、位移、倾角、环境量等基础监测终端，按需定制量程、接口、供电与通信方式。', tags: ['裂缝计', '位移计', '静力水准仪', '振弦应变'] },
  { icon: Microphone, title: '便携式声发射设备', desc: '便携主机、采集板卡、传感器与边缘分析模块一体化，快速部署、同步记录，特征识别与定位评估，检测结果可追溯。', tags: ['机动检测', '智能识别', '专项评估'] },
  { icon: VideoCamera, title: '巡检无人机与机械狗', desc: '面向大型园区、复杂结构与高风险区域，空地协同替代人工巡检，支持图像识别、路径规划、缺陷定位与自动报告。', tags: ['高危替人', '全域覆盖', '闭环运维'] }
])

const directions = ref(['裂纹监测', '应变监测', '振动监测', '声发射监测', '结构胶老化监测'])

const cases = ref([
  { tag: '填补国内空白', title: '工业动载监测', desc: '研发悬挂运输设备在线监测系统，针对重载钢吊车梁疲劳荷载问题，完成从测试装备到设计方法修正的全套技术研发。' },
  { tag: '快速应急响应', title: '监测数据展示', desc: '面对社会关注度高、现场响应时间短、结构状态判断难等多重挑战，围绕钢管混凝土脱空问题开展快速定制化监测。' },
  { tag: '2026 · 实时监测', title: '广西北港新材料有限公司', desc: '非接触式激光测振、红外热成像与外立面损伤数据快速获取，为重大突发事件应急处置提供高效、精准的智能诊断。' },
  { tag: '2026 · 特殊场景', title: '全国人民大会堂健康监测', desc: '支撑特殊场景的结构健康监测与运维，以高可靠设备与定制化方案服务国家级重点工程。' }
])

const coopModes = ref([
  { title: '设备定制采购', desc: '设备定制 · 快速交付 · 成本可控，面向具体工况量身定制监测终端。' },
  { title: '监测服务外包', desc: '方案实施 · 数据分析 · 报告输出，从部署到运维的全流程专业服务。' },
  { title: '联合研发 / 技术合作', desc: '联合攻关 · 成果共建 · 长期合作，深度联合共建产学研能力。' },
  { title: '试点项目合作', desc: '低门槛启动 · 案例共建 · 数据反馈，以轻量试点推动设备快速落地。' }
])

const whyUs = ref([
  { icon: School, title: '技术深度', desc: '依托高校科研积累，具备结构机理、监测方法、设备研发与数据分析能力。' },
  { icon: EditPen, title: '定制灵活', desc: '不是标准化成品套用，而是面向具体工况、具体问题和具体需求量身定制。' },
  { icon: Stopwatch, title: '响应快', desc: '团队扁平、沟通链路短，能够快速完成需求对接、方案迭代与项目推进。' },
  { icon: TrendCharts, title: '性价比高', desc: '依托学术团队成本优势，在保证技术质量的前提下，实现更优的投入产出比。' }
])

const CONTACT_EMAIL = 'qingzhenjiance@163.com'

const copyEmail = async () => {
  try {
    await navigator.clipboard.writeText(CONTACT_EMAIL)
    ElMessage.success('咨询邮箱已复制：' + CONTACT_EMAIL)
  } catch (e) {
    ElMessage.info('欢迎来信咨询：' + CONTACT_EMAIL)
  }
}

const goProducts = () => router.push('/products')
</script>

<style scoped>
.about-page {
  padding-top: 72px;
  background: #fff;
  min-height: 100vh;
  font-family: -apple-system, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  color: #1E293B;
}

.container { max-width: 1200px; margin: 0 auto; padding: 0 32px; }
.section { padding: 80px 0; }

/* ===== 页面头部 ===== */
.page-hero {
  position: relative;
  background:
    radial-gradient(900px 420px at 85% -20%, rgba(37,99,235,.28), transparent 70%),
    radial-gradient(700px 380px at 0% 110%, rgba(37,99,235,.18), transparent 70%),
    #0F1B33;
  padding: 96px 0 88px;
  overflow: hidden;
}
.page-hero::after {
  content: '';
  position: absolute; inset: 0;
  background-image: linear-gradient(rgba(255,255,255,.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.045) 1px, transparent 1px);
  background-size: 56px 56px;
  pointer-events: none;
}
.hero-inner { position: relative; z-index: 1; max-width: 1200px; margin: 0 auto; padding: 0 32px; }
.hero-eyebrow {
  display: inline-block; font-size: 12px; font-weight: 700; letter-spacing: 4px;
  color: #7DA6F5; text-transform: uppercase; margin-bottom: 20px;
  padding: 6px 14px; border: 1px solid rgba(125,166,245,.4); border-radius: 999px;
}
.page-hero h1 { font-size: 46px; font-weight: 800; color: #fff; margin: 0 0 14px; letter-spacing: 2px; }
.hero-org { font-size: 17px; color: #9DB4DC; margin: 0 0 18px; letter-spacing: 1px; }
.hero-desc { font-size: 17px; color: rgba(255,255,255,.72); margin: 0 0 30px; max-width: 640px; line-height: 1.8; }
.hero-desc strong { color: #fff; }
.hero-vision {
  display: inline-flex; align-items: center; gap: 10px;
  background: rgba(37,99,235,.16); border: 1px solid rgba(96,165,250,.4);
  border-radius: 12px; padding: 14px 22px; color: #CFE0FF; font-size: 15px; max-width: 720px;
}
.hero-vision .el-icon { color: #7DA6F5; flex-shrink: 0; }

/* ===== 通用标题 ===== */
.intro-head { margin-bottom: 44px; }
.intro-head.light h2 { color: #fff; }
.eyebrow { display: block; font-size: 12px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; color: #2563EB; margin-bottom: 10px; }
.eyebrow.light { color: #7DA6F5; }
.intro-head h2 { font-size: 32px; font-weight: 800; color: #0F1B33; margin: 0 0 10px; }
.head-sub { color: #64748B; font-size: 15px; margin: 0; }
.intro-head.light .head-sub { color: rgba(255,255,255,.6); }

/* ===== 我们是谁 ===== */
.intro-grid { display: grid; grid-template-columns: 1.15fr .85fr; gap: 48px; align-items: start; }
.intro-text p { font-size: 15.5px; line-height: 2; color: #475569; margin: 0 0 18px; text-align: justify; }
.intro-text strong { color: #2563EB; font-weight: 700; }
.intro-quote {
  display: flex; align-items: flex-start; gap: 10px;
  background: #F5F8FF; border-left: 4px solid #2563EB; border-radius: 0 12px 12px 0;
  padding: 16px 20px; color: #334155; font-size: 15px; font-weight: 600; line-height: 1.7;
}
.intro-quote .el-icon { color: #2563EB; margin-top: 3px; flex-shrink: 0; }
.position-cards { display: flex; flex-direction: column; gap: 16px; }
.position-card {
  display: flex; gap: 18px; padding: 22px 24px;
  background: #fff; border: 1px solid #EDF1F7; border-radius: 16px;
  transition: all .3s ease;
}
.position-card:hover { transform: translateX(6px); border-color: #BFD3F5; box-shadow: 0 12px 28px rgba(15,27,51,.08); }
.position-icon {
  width: 52px; height: 52px; border-radius: 14px; background: #F0F5FF; color: #2563EB;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all .3s ease;
}
.position-card:hover .position-icon { background: #2563EB; color: #fff; }
.position-card h3 { font-size: 16px; font-weight: 700; color: #0F1B33; margin: 0 0 6px; }
.position-card p { font-size: 13px; color: #64748B; line-height: 1.7; margin: 0; }

/* ===== 数据带 ===== */
.stats-band {
  background: radial-gradient(1000px 400px at 50% -30%, rgba(37,99,235,.3), transparent 70%), #0F1B33;
  padding: 56px 0;
}
.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; }
.stat-item { text-align: center; padding: 8px 12px; border-right: 1px solid rgba(255,255,255,.1); }
.stat-item:last-child { border-right: none; }
.stat-value { font-size: 42px; font-weight: 800; color: #fff; line-height: 1.2; }
.stat-value span { font-size: 18px; font-weight: 700; color: #7DA6F5; margin-left: 4px; }
.stat-label { font-size: 13px; color: rgba(255,255,255,.55); margin-top: 10px; line-height: 1.6; }

/* ===== 发展历程 ===== */
.section-history { background: #F7F9FD; }
.timeline { position: relative; display: flex; flex-direction: column; gap: 28px; padding-left: 150px; }
.timeline::before { content: ''; position: absolute; left: 178px; top: 12px; bottom: 12px; width: 2px; background: #D8E2F2; }
.timeline-item { position: relative; display: flex; align-items: flex-start; gap: 40px; }
.timeline-year {
  position: absolute; left: -150px; top: 14px; width: 110px; text-align: right;
  font-size: 30px; font-weight: 800; color: #2563EB; letter-spacing: 1px;
}
.timeline-dot {
  position: absolute; left: 22px; top: 22px; width: 14px; height: 14px; border-radius: 50%;
  background: #2563EB; box-shadow: 0 0 0 5px rgba(37,99,235,.15); z-index: 1;
}
.timeline-card {
  flex: 1; margin-left: 56px; background: #fff; border: 1px solid #EDF1F7; border-radius: 16px;
  padding: 24px 28px; transition: all .3s ease;
}
.timeline-card:hover { transform: translateY(-4px); box-shadow: 0 16px 32px rgba(15,27,51,.08); }
.timeline-card h3 { font-size: 18px; font-weight: 700; color: #0F1B33; margin: 0 0 10px; }
.timeline-card p { font-size: 14px; color: #64748B; line-height: 1.8; margin: 0; }

/* ===== 核心能力 ===== */
.product-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; margin-bottom: 28px; }
.product-card {
  background: #fff; border: 1px solid #EDF1F7; border-radius: 16px; padding: 30px 26px;
  transition: all .3s ease;
}
.product-card:hover { transform: translateY(-6px); box-shadow: 0 20px 40px rgba(15,27,51,.1); border-color: #D6E2F7; }
.product-icon {
  width: 58px; height: 58px; border-radius: 16px; background: #F0F5FF; color: #2563EB;
  display: flex; align-items: center; justify-content: center; margin-bottom: 20px; transition: all .3s ease;
}
.product-card:hover .product-icon { background: #2563EB; color: #fff; }
.product-card h3 { font-size: 18px; font-weight: 700; color: #0F1B33; margin: 0 0 10px; }
.product-card p { font-size: 13.5px; color: #64748B; line-height: 1.8; margin: 0 0 16px; }
.product-tags { display: flex; flex-wrap: wrap; gap: 8px; }
.product-tags span {
  font-size: 12px; color: #2563EB; background: #F0F5FF; border-radius: 999px; padding: 4px 12px; font-weight: 500;
}
.direction-strip {
  display: flex; flex-wrap: wrap; gap: 14px; justify-content: center;
  background: #F7F9FD; border: 1px dashed #C9D8F0; border-radius: 14px; padding: 20px 24px; margin-bottom: 20px;
}
.direction-item { display: inline-flex; align-items: center; gap: 8px; font-size: 14.5px; font-weight: 600; color: #334155; }
.direction-item .el-icon { color: #2563EB; }
.platform-line {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  color: #475569; font-size: 14px;
}
.platform-line .el-icon { color: #2563EB; }

/* ===== 典型案例（深色带） ===== */
.section-cases {
  background: radial-gradient(1100px 460px at 50% -10%, rgba(37,99,235,.25), transparent 70%), #0F1B33;
}
.case-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.case-card {
  background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.09); border-radius: 16px;
  padding: 26px 24px; transition: all .3s ease;
}
.case-card:hover { background: rgba(255,255,255,.09); transform: translateY(-5px); }
.case-tag {
  display: inline-block; font-size: 12px; font-weight: 700; color: #7DA6F5;
  border: 1px solid rgba(125,166,245,.45); border-radius: 999px; padding: 3px 12px; margin-bottom: 14px;
}
.case-card h3 { font-size: 16.5px; font-weight: 700; color: #fff; margin: 0 0 10px; }
.case-card p { font-size: 13px; color: rgba(255,255,255,.6); line-height: 1.8; margin: 0; }

/* ===== 合作模式 ===== */
.coop-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
.coop-card {
  background: #fff; border: 1px solid #EDF1F7; border-radius: 16px; padding: 28px 24px;
  transition: all .3s ease; position: relative; overflow: hidden;
}
.coop-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
  background: #2563EB; transform: scaleX(0); transform-origin: left; transition: transform .35s ease;
}
.coop-card:hover { transform: translateY(-6px); box-shadow: 0 18px 36px rgba(15,27,51,.1); }
.coop-card:hover::before { transform: scaleX(1); }
.coop-no { font-size: 26px; font-weight: 800; color: #BFD3F5; margin-bottom: 12px; }
.coop-card h3 { font-size: 16.5px; font-weight: 700; color: #0F1B33; margin: 0 0 10px; }
.coop-card p { font-size: 13px; color: #64748B; line-height: 1.7; margin: 0; }

/* ===== 为什么选择我们 ===== */
.section-why { background: #F7F9FD; }
.why-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
.why-card {
  background: #fff; border: 1px solid #EDF1F7; border-radius: 16px; padding: 30px 24px;
  text-align: center; transition: all .3s ease;
}
.why-card:hover { transform: translateY(-6px); box-shadow: 0 18px 36px rgba(15,27,51,.1); }
.why-icon {
  width: 56px; height: 56px; border-radius: 50%; background: #F0F5FF; color: #2563EB;
  display: flex; align-items: center; justify-content: center; margin: 0 auto 18px; transition: all .3s ease;
}
.why-card:hover .why-icon { background: #2563EB; color: #fff; transform: scale(1.08); }
.why-card h3 { font-size: 17px; font-weight: 700; color: #0F1B33; margin: 0 0 10px; }
.why-card p { font-size: 13px; color: #64748B; line-height: 1.7; margin: 0; }
.why-quote { text-align: center; margin: 36px 0 0; font-size: 16px; font-weight: 700; color: #2563EB; letter-spacing: 1px; }

/* ===== 联系 CTA ===== */
.section-contact { padding: 90px 0; }
.contact-inner { text-align: center; }
.contact-inner h2 { font-size: 34px; font-weight: 800; color: #0F1B33; margin: 0 0 12px; }
.contact-inner > p { font-size: 15px; color: #64748B; margin: 0 0 32px; }
.contact-actions { display: flex; justify-content: center; gap: 16px; flex-wrap: wrap; margin-bottom: 24px; }
.contact-actions .el-button--primary { background: #2563EB; border-color: #2563EB; }
.contact-actions .el-button--primary:hover { background: #1D4ED8; border-color: #1D4ED8; }
.contact-addr { display: inline-flex; align-items: center; gap: 6px; color: #94A3B8; font-size: 14px; margin: 0; }

/* ===== 响应式 ===== */
@media (max-width: 1000px) {
  .intro-grid { grid-template-columns: 1fr; }
  .product-cards, .case-grid { grid-template-columns: repeat(2, 1fr); }
  .coop-grid, .why-grid, .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .stat-item { border-right: none; }
  .page-hero h1 { font-size: 34px; }
}
@media (max-width: 640px) {
  .section { padding: 56px 0; }
  .container { padding: 0 20px; }
  .product-cards, .case-grid, .coop-grid, .why-grid, .stats-grid { grid-template-columns: 1fr; }
  .page-hero { padding: 64px 0 56px; }
  .page-hero h1 { font-size: 28px; }
  .timeline { padding-left: 0; }
  .timeline::before { left: 6px; }
  .timeline-year { position: static; width: auto; text-align: left; margin-bottom: 6px; }
  .timeline-item { flex-direction: column; gap: 8px; padding-left: 32px; }
  .timeline-dot { left: 0; top: 10px; }
  .timeline-card { margin-left: 0; }
}
</style>
